import React, { memo, useState } from "react";
import { convertHtmlString } from "../helper/convertor";
import { highlightHtml } from "../helper/highlightSearch";
import LanguageContext from "../helper/LanguageContext";
import { DE } from "../Languages/DE";
import { EN } from "../Languages/EN";

// Helper function to strip HTML tags and get plain text
const stripHtmlTags = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};

// Helper function to truncate HTML while preserving structure
const truncateHtml = (html, maxLength) => {
    if (!html) return '';
    
    const plainText = stripHtmlTags(html);
    if (plainText.length <= maxLength) return html;
    
    // Find a good break point in the plain text
    let breakPoint = maxLength;
    // Try to break at a space to avoid cutting words
    const spaceIndex = plainText.lastIndexOf(' ', maxLength);
    if (spaceIndex > maxLength * 0.7) {
        breakPoint = spaceIndex;
    }
    
    // Now we need to find where this breakpoint corresponds to in the HTML
    let plainIndex = 0;
    let htmlIndex = 0;
    let inTag = false;
    
    while (plainIndex < breakPoint && htmlIndex < html.length) {
        if (html[htmlIndex] === '<') {
            inTag = true;
        } else if (html[htmlIndex] === '>') {
            inTag = false;
        } else if (!inTag) {
            // Handle HTML entities
            if (html.substring(htmlIndex, htmlIndex + 6) === '&nbsp;') {
                htmlIndex += 5; // Will be incremented by 1 at end of loop
                plainIndex++;
            } else {
                plainIndex++;
            }
        }
        htmlIndex++;
    }
    
    // Skip past any remaining open tag
    while (htmlIndex < html.length && inTag) {
        if (html[htmlIndex] === '>') {
            inTag = false;
        }
        htmlIndex++;
    }
    
    let truncated = html.substring(0, htmlIndex);
    
    // Close any open tags
    const openTags = [];
    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
    let match;
    
    while ((match = tagRegex.exec(truncated)) !== null) {
        const fullTag = match[0];
        const tagName = match[1].toLowerCase();
        
        // Skip self-closing tags
        if (fullTag.endsWith('/>') || ['br', 'hr', 'img', 'input'].includes(tagName)) {
            continue;
        }
        
        if (fullTag.startsWith('</')) {
            // Closing tag - remove from stack
            const idx = openTags.lastIndexOf(tagName);
            if (idx !== -1) {
                openTags.splice(idx, 1);
            }
        } else {
            // Opening tag - add to stack
            openTags.push(tagName);
        }
    }
    
    // Close open tags in reverse order
    for (let i = openTags.length - 1; i >= 0; i--) {
        truncated += `</${openTags[i]}>`;
    }
    
    return truncated;
};

const ReadMore = ({ children, isInternal, maxLength = 300, searchQuery = "" }) => {
    const text = children;
    const [isReadMore, setIsReadMore] = useState(true);
    const { isGerm, strings } = React.useContext(LanguageContext);
    
    const toggleReadMore = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsReadMore(!isReadMore);
    };
    
    // Get plain text length to determine if we need truncation
    const plainText = stripHtmlTags(text);
    const needsTruncation = plainText.length > maxLength;
    
    // Process the text for display
    const processText = (htmlText) => {
        if (!htmlText) return '';
        let processed = convertHtmlString(
            htmlText
                .replace(/\n/g, "<br />")
                .replace(/\\\//g, "")
        );
        if (searchQuery) {
            processed = highlightHtml(processed, searchQuery);
        }
        return processed;
    };
    
    const truncatedHtml = needsTruncation ? truncateHtml(text, maxLength) : text;
    
    return (
        <div className="read-more-text">
            {isReadMore && needsTruncation ? (
                <div 
                    className="m-0" 
                    dangerouslySetInnerHTML={{ __html: processText(truncatedHtml) }}
                />
            ) : (
                <div 
                    className="m-0" 
                    dangerouslySetInnerHTML={{ __html: processText(text) }}
                />
            )}
            {needsTruncation && (
                <span 
                    className={`read-more-toggle ml-1 ${isInternal ? 'read-more-internal' : 'read-more-external'}`}
                    onClick={toggleReadMore}
                >
                    {isReadMore ? `...${strings.see_more}` : ` ${strings.see_less}`}
                </span>
            )}
        </div>
    );
};

export default memo(ReadMore);
